// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { Rectangle } from '@shared/commons/Geometry';
import { TerceraChartBaseRenderer } from './TerceraChartBaseRenderer';
import { MouseButtons } from '@front/controls/UtilsClasses/ControlsUtils';
import { TerceraChartAction, TerceraChartActionEnum } from '../TerceraChartAction';
import { ThemeManager } from '@front/controls/misc/ThemeManager';
import { Pen, SolidBrush } from '@shared/commons/Graphics';
import { LayersEnum } from '../Utils/LayersEnum';
export class TerceraChartZoomRenderer extends TerceraChartBaseRenderer {
    constructor(chart, chartActionProcessor) {
        super(chart);
        this.toBeginRect = new Rectangle();
        this.navigationBtnsContainer = new Rectangle();
        this.opacity = 0;
        this.zoomInRect = new Rectangle();
        this.zoomOutRect = new Rectangle();
        this.scalingByRectRect = new Rectangle();
        this.Rectangle = new Rectangle();
        this.CurrentVisibleButtons = ChartZoomRendererButtons.All;
        this.CurrentHoverButtonType = ChartZoomRendererButtons.None;
        this.assignLayer = LayersEnum.Tools;
        this.chartActionProcessor = chartActionProcessor;
        this.SetClassName('TerceraChartZoomRenderer');
    }
    Draw(gr, window, windowsContainer, advParams = null) {
        if (this.opacity <= 0) {
            return;
        }
        const MARGIN = TerceraChartZoomRenderer.MARGIN;
        const SIZE = TerceraChartZoomRenderer.SIZE;
        const spaceBetweenBtns = TerceraChartZoomRenderer.SPACEBETWEENBTNS;
        let x = this.Rectangle.X + MARGIN;
        const y = this.Rectangle.Y + MARGIN;
        let state;
        let btnsCount = 0;
        gr.globalAlpha = this.opacity;
        if ((this.CurrentVisibleButtons & ChartZoomRendererButtons.StepZoomOut) !== 0) {
            this.zoomOutRect.X = x;
            this.zoomOutRect.Y = y;
            this.zoomOutRect.Width = SIZE;
            this.zoomOutRect.Height = SIZE;
            x += SIZE + spaceBetweenBtns;
            let brushColor = ThemeManager.colors.chartZoomButtonBackground;
            let borderColor = ThemeManager.colors.chartZoomButtonBorder;
            let im = TerceraChartZoomRenderer.zoomOutImage.image;
            state = this.chartActionProcessor.GetTerceraChartActionState(TerceraChartAction.Create(TerceraChartActionEnum.Zoom, ChartZoomRendererButtons.StepZoomOut));
            if (!state.Enabled) {
                brushColor = ThemeManager.colors.chartZoomButtonBackgroundDisabled;
                borderColor = ThemeManager.colors.chartZoomButtonBorderDisabled;
                im = TerceraChartZoomRenderer.zoomOutImageDisabled.image;
            }
            else if (this.CurrentHoverButtonType === ChartZoomRendererButtons.StepZoomOut) {
                brushColor = ThemeManager.colors.chartZoomButtonBackgroundHovered;
                borderColor = ThemeManager.colors.chartZoomButtonBorderHovered;
                im = TerceraChartZoomRenderer.zoomOutImageHovered.image;
            }
            const brush = new SolidBrush(brushColor);
            const border = new Pen(borderColor);
            gr.RoundRectangle(this.zoomOutRect, 4, brush, border);
            gr.DrawCenteredImage(im, this.zoomOutRect);
            btnsCount++;
        }
        if ((this.CurrentVisibleButtons & ChartZoomRendererButtons.StepZoomIn) !== 0) {
            this.zoomInRect.X = x;
            this.zoomInRect.Y = y;
            this.zoomInRect.Width = SIZE;
            this.zoomInRect.Height = SIZE;
            x += SIZE + spaceBetweenBtns;
            let brushColor = ThemeManager.colors.chartZoomButtonBackground;
            let borderColor = ThemeManager.colors.chartZoomButtonBorder;
            let im = TerceraChartZoomRenderer.zoomInImage.image;
            state = this.chartActionProcessor.GetTerceraChartActionState(TerceraChartAction.Create(TerceraChartActionEnum.Zoom, ChartZoomRendererButtons.StepZoomIn));
            if (!state.Enabled) {
                brushColor = ThemeManager.colors.chartZoomButtonBackgroundDisabled;
                borderColor = ThemeManager.colors.chartZoomButtonBorderDisabled;
                im = TerceraChartZoomRenderer.zoomInImageDisabled.image;
            }
            else if (this.CurrentHoverButtonType === ChartZoomRendererButtons.StepZoomIn) {
                brushColor = ThemeManager.colors.chartZoomButtonBackgroundHovered;
                borderColor = ThemeManager.colors.chartZoomButtonBorderHovered;
                im = TerceraChartZoomRenderer.zoomInImageHovered.image;
            }
            const brush = new SolidBrush(brushColor);
            const border = new Pen(borderColor);
            gr.RoundRectangle(this.zoomInRect, 4, brush, border);
            gr.DrawCenteredImage(im, this.zoomInRect);
            btnsCount++;
        }
        if ((this.CurrentVisibleButtons & ChartZoomRendererButtons.ToBegin) !== 0) {
            this.toBeginRect.X = x;
            this.toBeginRect.Y = y;
            this.toBeginRect.Width = SIZE;
            this.toBeginRect.Height = SIZE;
            let brushColor = ThemeManager.colors.chartZoomButtonBackground;
            let borderColor = ThemeManager.colors.chartZoomButtonBorder;
            let im = TerceraChartZoomRenderer.toBeginImage.image;
            state = this.chartActionProcessor.GetTerceraChartActionState(TerceraChartAction.Create(TerceraChartActionEnum.ToBegin, ChartZoomRendererButtons.ToBegin));
            if (!state.Enabled) {
                brushColor = null;
                borderColor = null;
                im = null;
            }
            else if (this.CurrentHoverButtonType === ChartZoomRendererButtons.ToBegin) {
                brushColor = ThemeManager.colors.chartZoomButtonBackgroundHovered;
                borderColor = ThemeManager.colors.chartZoomButtonBorderHovered;
                im = TerceraChartZoomRenderer.toBeginImageHovered.image;
            }
            if (isValidString(brushColor)) {
                const brush = new SolidBrush(brushColor);
                const border = new Pen(borderColor);
                gr.RoundRectangle(this.toBeginRect, 4, brush, border);
                gr.DrawCenteredImage(im, this.toBeginRect);
                btnsCount++;
            }
        }
        this.navigationBtnsContainer.X = this.zoomOutRect.X + SIZE / 2;
        this.navigationBtnsContainer.Y = this.zoomOutRect.Y;
        this.navigationBtnsContainer.Width = (SIZE + spaceBetweenBtns) * (btnsCount - 1);
        this.navigationBtnsContainer.Height = SIZE;
        gr.globalAlpha = 1.0;
    }
    ThemeChanged() {
        super.ThemeChanged();
        TerceraChartZoomRenderer.ThemeChangedStatic();
    }
    static ThemeChangedStatic() {
        TerceraChartZoomRenderer.zoomInImage = ThemeManager.NewImages.zoomInBtn;
        TerceraChartZoomRenderer.zoomInImageHovered = ThemeManager.NewImages.zoomInBtnHovered;
        TerceraChartZoomRenderer.zoomInImageDisabled = ThemeManager.NewImages.zoomInBtnDisabled;
        TerceraChartZoomRenderer.zoomOutImage = ThemeManager.NewImages.zoomOutBtn;
        TerceraChartZoomRenderer.zoomOutImageHovered = ThemeManager.NewImages.zoomOutBtnHovered;
        TerceraChartZoomRenderer.zoomOutImageDisabled = ThemeManager.NewImages.zoomOutBtnDisabled;
        TerceraChartZoomRenderer.toBeginImage = ThemeManager.NewImages.toBeginBtn;
        TerceraChartZoomRenderer.toBeginImageHovered = ThemeManager.NewImages.toBeginBtnHovered;
    }
    GetCurrentHoverButton(pt) {
        if (this.zoomInRect.Contains(pt.X, pt.Y))
            return ChartZoomRendererButtons.StepZoomIn;
        else if (this.zoomOutRect.Contains(pt.X, pt.Y))
            return ChartZoomRendererButtons.StepZoomOut;
        else if (this.toBeginRect.Contains(pt.X, pt.Y) &&
            this.chartActionProcessor.GetTerceraChartActionState(TerceraChartAction.Create(TerceraChartActionEnum.ToBegin, ChartZoomRendererButtons.ToBegin)).Enabled)
            return ChartZoomRendererButtons.ToBegin;
        else if (this.navigationBtnsContainer.Contains(pt.X, pt.Y))
            return ChartZoomRendererButtons.Container;
        // else if (this.scalingByRectRect.Contains(pt.X, pt.Y)) return ChartZoomRendererButtons.ScalingByRectangle;
        else
            return ChartZoomRendererButtons.None;
    }
    ProcessMouseDown(e) {
        if (e.Button !== MouseButtons.Left || !this.Visible)
            return false;
        const newHoverButtonType = this.GetCurrentHoverButton(e.Location);
        if (newHoverButtonType === ChartZoomRendererButtons.None)
            return false;
        if (newHoverButtonType === ChartZoomRendererButtons.ToBegin) {
            return this.chart.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.ToBegin, this));
        }
        return this.chartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.Zoom, newHoverButtonType));
    }
    ProcessMouseMove(e) {
        const oldVisible = this.Visible;
        this.Visible = this.Rectangle.Contains(e.Location.X, e.Location.Y);
        if (this.Visible && !oldVisible) {
            this.animationOfAppearance();
        }
        else if (!this.Visible) {
            if (oldVisible) {
                this.animationOfDisappearance();
            }
            return;
        }
        const newHoverButtonType = this.GetCurrentHoverButton(e.Location);
        if (newHoverButtonType !== this.CurrentHoverButtonType) {
            this.CurrentHoverButtonType = newHoverButtonType;
            e.NeedRedraw = LayersEnum.All;
        }
        return super.ProcessMouseMove(e);
    }
    ProcessMouseWheel(e) {
        if (e._baseEvent.ctrlKey || // Ignore if the Ctrl key is pressed.
            Math.abs(e._baseEvent.deltaX) >= Math.abs(e._baseEvent.deltaY) // Ignore if horizontal scroll dominates or it's purely horizontal.
        ) {
            return false;
        }
        const step = e.Delta / 120;
        if (step > 0) {
            this.chartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.Zoom, ChartZoomRendererButtons.ZoomIn));
        }
        else {
            this.chartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.Zoom, ChartZoomRendererButtons.ZoomOut));
        }
        return true;
    }
    ProcessMultiTouches(e) {
        const step = e.delta;
        if (step > 0) {
            this.chartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.Zoom, ChartZoomRendererButtons.ZoomIn));
        }
        else {
            this.chartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.Zoom, ChartZoomRendererButtons.ZoomOut));
        }
        return true;
    }
    ProcessMouseLeave(e) {
        super.ProcessMouseLeave(e);
        this.CurrentHoverButtonType = ChartZoomRendererButtons.None;
        if (this.Visible) {
            this.animationOfDisappearance();
        }
    }
    Do_ZoomIn() {
        this.chartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.Zoom, ChartZoomRendererButtons.ZoomIn));
    }
    Do_ZoomOut() {
        this.chartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.Zoom, ChartZoomRendererButtons.ZoomOut));
    }
    Dispose() {
        this.chartActionProcessor = null;
        clearInterval(this.opacityTimer);
        this.opacityTimer = null;
        super.Dispose();
    }
    GetTooltip(e) {
        if (!this.Visible || this.CurrentHoverButtonType === ChartZoomRendererButtons.None)
            return null;
        let state;
        if (this.CurrentHoverButtonType === ChartZoomRendererButtons.ToBegin) {
            state = this.chartActionProcessor.GetTerceraChartActionState(TerceraChartAction.Create(TerceraChartActionEnum.ToBegin, this.CurrentHoverButtonType));
        }
        else {
            state = this.chartActionProcessor.GetTerceraChartActionState(TerceraChartAction.Create(TerceraChartActionEnum.Zoom, this.CurrentHoverButtonType));
        }
        if ((state === null || state === void 0 ? void 0 : state.ToolTiplocKey) != null) {
            return Resources.getResource(state.ToolTiplocKey);
        }
        return null;
    }
    animationOfAppearance() {
        clearInterval(this.opacityTimer);
        this.opacityTimer = setInterval(() => {
            this.opacity += 0.1;
            this.chart.IsDirty(this.assignLayer);
            if (this.opacity >= 1) {
                clearInterval(this.opacityTimer);
                this.opacityTimer = null;
            }
        }, 50);
    }
    animationOfDisappearance() {
        clearInterval(this.opacityTimer);
        this.opacityTimer = setInterval(() => {
            this.opacity -= 0.1;
            this.chart.IsDirty(this.assignLayer);
            if (this.opacity <= 0) {
                clearInterval(this.opacityTimer);
                this.opacityTimer = null;
            }
        }, 50);
    }
}
TerceraChartZoomRenderer.MARGIN = 100;
TerceraChartZoomRenderer.SIZE = 32;
TerceraChartZoomRenderer.SPACEBETWEENBTNS = 8;
export var ChartZoomRendererButtons;
(function (ChartZoomRendererButtons) {
    ChartZoomRendererButtons[ChartZoomRendererButtons["None"] = 0] = "None";
    ChartZoomRendererButtons[ChartZoomRendererButtons["StepZoomIn"] = 1] = "StepZoomIn";
    ChartZoomRendererButtons[ChartZoomRendererButtons["ZoomIn"] = 2] = "ZoomIn";
    ChartZoomRendererButtons[ChartZoomRendererButtons["StepZoomOut"] = 3] = "StepZoomOut";
    ChartZoomRendererButtons[ChartZoomRendererButtons["ZoomOut"] = 4] = "ZoomOut";
    ChartZoomRendererButtons[ChartZoomRendererButtons["ScalingByRectangle"] = 8] = "ScalingByRectangle";
    ChartZoomRendererButtons[ChartZoomRendererButtons["ToBegin"] = 16] = "ToBegin";
    ChartZoomRendererButtons[ChartZoomRendererButtons["All"] = 31] = "All";
    ChartZoomRendererButtons[ChartZoomRendererButtons["Container"] = 32] = "Container";
})(ChartZoomRendererButtons || (ChartZoomRendererButtons = {}));
